import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { UPLOAD } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const CHUNK = 4 * 1024 * 1024; // 4 MB
const MAX_MB = 60;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function fmtName(name) {
  const stem = name.replace(/\.[^.]+$/, "");
  return stem.slice(0, 80);
}

export function UploadDataDialog({ workspaceId, children, onUploaded, defaultDestination = "new" }) {
  const navigate = useNavigate();
  const { refresh, setActive } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [destination, setDestination] = useState(defaultDestination);
  const [datasetName, setDatasetName] = useState("");
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | uploading | processing
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null); setDatasetName(""); setBusy(false);
    setPhase("idle"); setProgress(0); setDestination(defaultDestination);
  };

  const pickFile = (f) => {
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!lower.endsWith(".csv") && !lower.endsWith(".parquet")) {
      toast.error("Only .csv and .parquet files are supported.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large (max ${MAX_MB} MB).`);
      return;
    }
    setFile(f);
    setDatasetName(fmtName(f.name));
  };

  const onDrop = (e) => {
    e.preventDefault();
    pickFile(e.dataTransfer.files?.[0]);
  };

  const doUpload = useCallback(async () => {
    if (!file || busy) return;
    if (destination === "current" && !workspaceId) {
      toast.error("No active workspace.");
      return;
    }
    setBusy(true);
    setPhase("uploading");
    setProgress(2);
    const lower = file.name.toLowerCase();
    let fmt = "csv";
    if (lower.endsWith(".parquet")) fmt = "parquet";
    else if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) fmt = "xlsx";
    try {
      const { data: initData } = await api.post("/uploads/init", {
        filename: file.name, size: file.size, file_format: fmt,
      });
      const uploadId = initData.upload_id;
      const total = Math.max(1, Math.ceil(file.size / CHUNK));
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", String(i));
        fd.append("chunk", blob, file.name);
        await api.post("/uploads/chunk", fd);
        setProgress(Math.round(((i + 1) / total) * 45));
      }

      setPhase("processing");
      setProgress(48);
      const { data } = await api.post("/uploads/complete", {
        upload_id: uploadId,
        destination,
        workspace_id: workspaceId,
        dataset_name: datasetName || fmtName(file.name),
      });

      // Poll the connect pipeline so we land in chat only when it's queryable.
      const jobId = data.job_id;
      for (let i = 0; i < 120; i++) {
        const { data: job } = await api.get(`/jobs/${jobId}`);
        setProgress(50 + Math.round((job.progress || 0) / 2));
        if (job.status === "completed") break;
        if (job.status === "failed") throw new Error(job.error || "Processing failed.");
        await sleep(1500);
      }
      setProgress(100);
      toast.success(`“${datasetName || file.name}” is ready to chat with.`);

      if (destination === "new") {
        await refresh();
        setActive(data.workspace_id);
        navigate("/chat");
      } else {
        navigate(`/chat?source=${data.source_id}`);
      }
      onUploaded?.(data);
      setOpen(false);
      reset();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d && d.message) || e.message || "Upload failed.";
      toast.error(msg);
      setBusy(false);
      setPhase("idle");
      setProgress(0);
    }
  }, [file, busy, destination, workspaceId, datasetName, navigate, refresh, setActive, onUploaded]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) { setOpen(o); if (!o) reset(); } }}>
      <DialogTrigger asChild>
        {children || (
          <Button
            data-testid={UPLOAD.trigger}
            className="rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
          >
            <UploadCloud className="mr-2 h-4 w-4" /> Upload data
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid={UPLOAD.dialog} className="font-mono sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload a dataset</DialogTitle>
          <DialogDescription>
            Drop a CSV, XLSX, or Parquet file and chat with your data in seconds. Max {MAX_MB} MB.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Dropzone */}
          <div
            data-testid={UPLOAD.dropzone}
            onClick={() => !file && inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed px-6 py-8 text-center transition-colors ${
              file ? "border-accent/50 bg-accent/5" : "border-border/70 hover:border-accent/50"
            }`}
          >
            <input
              ref={inputRef}
              data-testid={UPLOAD.fileInput}
              type="file"
              accept=".csv,.parquet,.xlsx,.xls"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
            {file ? (
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="h-6 w-6 text-accent" />
                <div className="text-left">
                  <p className="text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                {!busy && (
                  <button
                    onClick={(e) => { e.stopPropagation(); reset(); }}
                    className="ml-2 rounded-full p-1 text-muted-foreground hover:text-foreground"
                    aria-label="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : (
              <>
                <UploadCloud className="h-7 w-7 text-accent" />
                <p className="mt-3 text-sm text-foreground/90">Click to browse or drag a file here</p>
                <p className="mt-1 text-xs text-muted-foreground">.csv · .xlsx · .parquet</p>
              </>
            )}
          </div>

          {file && !busy && (
            <>
              <div className="space-y-2">
                <Label htmlFor="dataset-name" className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
                  Dataset name
                </Label>
                <Input
                  id="dataset-name"
                  data-testid={UPLOAD.datasetName}
                  value={datasetName}
                  onChange={(e) => setDatasetName(e.target.value)}
                  className="text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Where should it live?</Label>
                <RadioGroup value={destination} onValueChange={setDestination} className="gap-2">
                  <label
                    data-testid={UPLOAD.destNew}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm ${
                      destination === "new" ? "border-accent/60 bg-accent/5" : "border-border/60"
                    }`}
                  >
                    <RadioGroupItem value="new" className="mt-0.5" />
                    <span>
                      <span className="font-medium">New dataset workspace</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Isolated glossary — switch to it from the workspace picker.
                      </span>
                    </span>
                  </label>
                  <label
                    data-testid={UPLOAD.destCurrent}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm ${
                      destination === "current" ? "border-accent/60 bg-accent/5" : "border-border/60"
                    } ${!workspaceId ? "pointer-events-none opacity-50" : ""}`}
                  >
                    <RadioGroupItem value="current" className="mt-0.5" disabled={!workspaceId} />
                    <span>
                      <span className="font-medium">Current workspace</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Added as a source — pick it from the dataset selector in chat.
                      </span>
                    </span>
                  </label>
                </RadioGroup>
              </div>
            </>
          )}

          {busy && (
            <div className="space-y-2" data-testid={UPLOAD.progress}>
              <Progress value={progress} />
              <p className="text-xs text-muted-foreground">
                {phase === "uploading" ? "Uploading file…" : "Analyzing your data…"} {progress}%
              </p>
            </div>
          )}

          <Button
            data-testid={UPLOAD.submit}
            onClick={doUpload}
            disabled={!file || busy}
            className="w-full rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
            {busy ? "Working…" : "Upload & analyze"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
